import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Users, 
  UserCheck, 
  FileText, 
  DollarSign, 
  LogOut,
  Menu,
  X,
  Zap,
  Calculator,
  Shield,
  Factory,
  BarChart3,
  Plus,
  Search,
  Settings,
  Sparkles,
  ClipboardList
} from "lucide-react";

export default function Layout({ children, user, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState(null);

  // Fetch the logged-in user's partner status to hide "Novo Cooperado" when suspended
  useEffect(() => {
    let cancelled = false;
    if (user?.id && user?.role !== 'admin' && user?.role !== 'operador'
        && user?.role !== 'usina' && user?.role !== 'usineiro'
        && user?.role !== 'partner_registration' && user?.role !== 'powerplant_registration') {
      axiosInstance
        .get(`/partners/by-user/${user.id}`)
        .then((res) => { if (!cancelled) setPartnerStatus(res.data?.status || null); })
        .catch(() => { if (!cancelled) setPartnerStatus(null); });
    } else {
      setPartnerStatus(null);
    }
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  const isPartnerSuspended = partnerStatus === 'suspended';

  // Se for usuário de cadastro de parceiros, mostrar apenas a página de Parceiros
  // Se for usuário de usina, mostrar apenas o relatório de medição
  // Se for usineiro, mostrar apenas o relatório de usinas
  const navigation = user?.role === 'partner_registration' 
    ? [{ name: "Cadastro de Parceiro", href: "/partners", icon: Users, testId: "nav-partners" }]
    : user?.role === 'powerplant_registration'
    ? [{ name: "Nova Usina", href: "/powerplants", icon: Factory, testId: "nav-powerplants" }]
    : user?.role === 'usina'
    ? [{ name: "Relatório de Medição", href: "/powerplant-report", icon: FileText, testId: "nav-powerplant-report" }]
    : user?.role === 'usineiro'
    ? [{ name: "Relatório de Usinas", href: "/powerplants-billing", icon: FileText, testId: "nav-powerplants-billing" }]
    : [
        { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
        { name: "Parceiros", href: "/partners", icon: Users, testId: "nav-partners" },
        { name: "Cooperados", href: "/cooperatives", icon: UserCheck, testId: "nav-cooperatives" },
        // Hide "Novo Cooperado" for suspended partners
        ...(isPartnerSuspended ? [] : [{ name: "Novo Cooperado", href: "/cooperatives/new", icon: Plus, testId: "nav-new-cooperative" }]),
        ...(user?.role === 'admin' ? [
          { name: "Gestão de Cooperados", href: "/cooperatives-management", icon: ClipboardList, testId: "nav-cooperatives-management" },
          { name: "Consulta Cooperado", href: "/consulta-cooperado", icon: Search, testId: "nav-consulta-cooperado" },
          { name: "Consulta Parceiro", href: "/consulta-parceiro", icon: Search, testId: "nav-consulta-parceiro" },
        ] : []),
        { name: "Comissões", href: "/commissions", icon: DollarSign, testId: "nav-commissions" },
        { name: "Simulador", href: "/simulator", icon: Calculator, testId: "nav-simulator" },
        { name: "Simulador Grupo A", href: "/simulator-group-a", icon: Calculator, testId: "nav-simulator-group-a" },
        ...(user?.is_master ? [
          { name: "Simulador em Lote (IA)", href: "/simulator-bulk", icon: Sparkles, testId: "nav-simulator-bulk" },
        ] : []),
        ...(user?.role === 'admin' || user?.role === 'operador' ? [
          { name: "Gráficos", href: "/analytics", icon: BarChart3, testId: "nav-analytics" },
        ] : []),
        ...(user?.is_master || user?.role === 'operador' ? [
          { name: "Usinas", href: "/powerplants", icon: Factory, testId: "nav-powerplants" },
        ] : []),
        ...(user?.role === 'admin' || user?.role === 'operador' ? [
          { name: "Usuários", href: "/users", icon: Shield, testId: "nav-users" }
        ] : []),
        ...(user?.role === 'admin' ? [
          { name: "Faturas", href: "/invoices", icon: FileText, testId: "nav-invoices" },
        ] : []),
        ...(user?.is_master ? [
          { name: "Relatório Usinas", href: "/powerplants-billing", icon: FileText, testId: "nav-powerplants-billing" },
          { name: "Configurações", href: "/distributor-settings", icon: Settings, testId: "nav-distributor-settings" },
        ] : []),
      ];

  return (
    <div className="min-h-screen flex">
      {/* Sidebar for desktop */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-white border-r border-gray-200">
        <div className="flex flex-col items-center gap-3 p-6 border-b border-gray-200">
          <img 
            src="/enercooper-logo.png" 
            alt="Enercooper" 
            className="w-48 h-auto"
          />
          <p className="text-xs text-gray-500">
            {user?.is_master ? 'Master' : user?.role === 'admin' ? 'Admin' : user?.role === 'operador' ? 'Operador' : user?.role === 'usina' ? 'Usina' : user?.role === 'usineiro' ? 'Usineiro' : user?.role === 'powerplant_registration' ? 'Cadastro de Usina' : user?.role === 'partner_registration' ? 'Cadastro de Parceiro' : 'Usuário'}
          </p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={item.testId}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-gray-700">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          <Button
            data-testid="logout-button"
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)}></div>
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <img 
                src="/enercooper-logo.png" 
                alt="Enercooper" 
                className="w-32 h-auto"
              />
              <button onClick={() => setSidebarOpen(false)} className="text-gray-500">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <nav className="flex-1 p-4 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? "bg-gradient-to-r from-blue-500 to-cyan-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="p-4 border-t border-gray-200">
              <div className="mb-3 px-4">
                <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <Button
                onClick={onLogout}
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sair
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <header className="md:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-gray-600">
            <Menu className="w-6 h-6" />
          </button>
          <img 
            src="/enercooper-logo.png" 
            alt="Enercooper" 
            className="h-8 w-auto"
          />
          <div className="w-6"></div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
}